<template>
    <Dialog v-model:open="opnened">
        <DialogTrigger> <slot /> </DialogTrigger>
        <DialogContent>
            <Transition name="slide-fade-top" mode="out-in">
                <div v-if="adding" class="border-iron-200 px-6 py-4 border-b">
                    <button class="gap-1 flex" @click="adding = false">
                        <ArrowLeft
                            :size="20"
                            class="text-z-primary-600"
                        ></ArrowLeft>
                        <p class="text-sm font-medium text-z-primary-600">
                            {{ $t("go_back") }}
                        </p>
                    </button>
                </div>
            </Transition>

            <Transition name="slide-fade-top" mode="out-in">
                <DialogHeader v-if="!adding">
                    <template #icon>
                        <CreditCard
                            class="text-z-primary-600"
                            stroke-width="2.25"
                            :size="20"
                        />
                    </template>
                    <DialogTitle>
                        {{ $t("billing.card.update_payment_method") }}
                    </DialogTitle>
                    <DialogDescription>
                        {{
                            $t("billing.card.update_payment_method_description")
                        }}
                    </DialogDescription>
                </DialogHeader>
                <DialogHeader v-else>
                    <template #icon>
                        <CreditCard
                            class="text-z-primary-600"
                            stroke-width="2.25"
                            :size="20"
                        />
                    </template>
                    <DialogTitle>
                        {{ $t("billing.card.new_payment_method") }}</DialogTitle
                    >
                    <DialogDescription>
                        {{ $t("billing.card.new_payment_method_description") }}
                    </DialogDescription>
                </DialogHeader>
            </Transition>

            <div class="px-6 pt-6">
                <Transition name="slide-fade-top" mode="out-in">
                    <div v-if="!adding">
                        <div
                            class="ease-outborder-iron-200 group my-4 rounded-xl p-4 border transition-all duration-100"
                            v-for="method of orderedPaymentMethods"
                            :key="method.id"
                        >
                            <UpdatePaymentMethod
                                :default-payment-method
                                :active-plan
                                :method
                            />
                        </div>

                        <Button
                            @click="adding = true"
                            class="gap-1"
                            variant="ghost"
                            ><Plus :size="20"></Plus>
                            {{ $t("billing.card.add_another") }}
                        </Button>
                    </div>

                    <div v-else>
                        <AddPaymentMethod
                            :clientSecret
                            :teamId
                            :billing-address
                            @method-added="adding = false"
                            @close="adding = false"
                        />
                    </div>
                </Transition>
            </div>

            <DialogFooter v-if="!adding">
                <DialogClose>
                    <Button variant="outline"> {{ $t("close") }}</Button>
                </DialogClose>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { Button } from "@/components/ui/button";
import { ref, computed } from "vue";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogClose,
} from "@/components/ui/dialog-v2";
import { ArrowLeft, CreditCard, Plus, Receipt, Train } from "lucide-vue-next";
import AddPaymentMethod from "./AddPaymentMethod.vue";
import type { BillingAddress, PaymentMethod, Plan } from "../types";
import UpdatePaymentMethod from "./UpdatePaymentMethod.vue";

const adding = ref(false);
const opnened = ref(false);

interface Props {
    teamId?: number;
    clientSecret: string;
    paymentMethods: PaymentMethod[];
    defaultPaymentMethod?: PaymentMethod;
    billingAddress: BillingAddress;
    activePlan?: Plan;
}

const props = defineProps<Props>();

const orderedPaymentMethods = computed(() =>
    props.paymentMethods.sort((a, b) => {
        if (a.id === props.defaultPaymentMethod?.id) {
            return -1;
        }

        if (b.id === props.defaultPaymentMethod?.id) {
            return 1;
        }

        return 0;
    }),
);
</script>

<style>
.slide-fade-top-enter-active {
    transition: all 0.2s ease-out;
}

.slide-fade-top-leave-active {
    transition: all 0.1s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-top-enter-from {
    transform: translateY(-12px);
    opacity: 0;
}
.slide-fade-top-leave-to {
    transform: translateY(-12px);
    opacity: 0;
}
</style>
